import React, { useMemo } from "react";
import Loader from "../../components/Loader";
import { useLocation, useNavigate } from "react-router-dom";
import InnerBannerBreadcrumb from "../../components/shared/breadcrumb/InnerBannerBreadcrumb";
import { useLanguageTranslation } from "../../hooks/useLanguageTranslation";
import { useSelector } from "react-redux";
import { useGetProtectedApiDataQuery } from "../../redux/services/apiSlice";
import { getLatestDateFromData } from "../../Utils/utils";
import PageLastUpdated from "../../components/shared/PageLastUpdated";
import { useTableResponsive } from "../../hooks/useTableResponsive";
import useSeo from "../../hooks/useSeo";

const ThermalPowerPage = () => {
  const { getText } = useLanguageTranslation();
  const { languageType } = useSelector((state) => state.homeSlice);

  const navigate = useNavigate();
  const { data: energyAssetsData = [], isLoading: energyAssetsIsLoading } =
    useGetProtectedApiDataQuery({ url: "energy-assets", lang: languageType });
  useTableResponsive(energyAssetsData);

  const lastUpdatedOn = useMemo(() => {
    return getLatestDateFromData(energyAssetsData);
  }, [energyAssetsData]);

  // for SEO
  const { pathname } = useLocation();
  const { innerBannerTitle, innerBannerImage } = useSeo({ pathname });

  const thermalPowerPlants = energyAssetsData.filter(
    (plant) => plant.field_category_page_assets === "Thermal Power",
  );

  const completedPlants = thermalPowerPlants.filter(
    (plant) => plant.field_status === "Completed",
  );

  const surveyPlants = thermalPowerPlants.filter(
    (plant) => plant.field_status === "Survey and Investigation (S&amp;I)",
  );

  const underConstructionPlants = thermalPowerPlants.filter(
    (plant) => plant.field_status === "Under Construction",
  );

  const calculateSubtotal = (plants) =>
    plants.reduce(
      (acc, plant) => acc + parseFloat(plant.field_plant_size || 0),
      0,
    );

  const completedSubtotal = calculateSubtotal(completedPlants);
  const surveySubtotal = calculateSubtotal(surveyPlants);
  const underConstructionSubtotal = calculateSubtotal(underConstructionPlants);
  const grandTotal =
    completedSubtotal + surveySubtotal + underConstructionSubtotal;

  if (energyAssetsIsLoading) {
    return <Loader />;
  }

  return (
    <>
      <InnerBannerBreadcrumb
        bannerImage={innerBannerImage}
        title={getText("Power Generation", "विद्युत उत्पादन")}
        titleLink="/power-generation"
        levelTwo={true}
        titleTwo={innerBannerTitle}
        levelThree={false}
        isActive={innerBannerTitle}
      />

      <div className="inner-container">
        <div className="container">
          <div className="container">
            <div className="row mb-4">
              <div className="col-12 col-sm-12 col-md-12 col-lg-12 wow zoomIn">
                <div className="table-responsive">
                  <div className="view view-projects view-id-projects view-display-id-block_2 view-dom-id-3f1a183aee063f414cf2ce525c69cf48">
                    <div className="view-content"></div>
                  </div>
                  <table className="table table-striped custom-table">
                    <thead>
                      <tr>
                        <th style={{ width: "80px" }}>
                          {getText("S.No.", "क्र. सं.")}
                        </th>
                        <th>{getText("Project Name", "परियोजना का नाम")}</th>
                        <th>{getText("Location / State", "स्थान / राज्य")}</th>
                        <th>
                          {getText(
                            "Installed Capacity(MW)",
                            "स्थापित क्षमता (मेगावाट)",
                          )}
                        </th>
                        <th className="no-wrap-td">
                          {getText(
                            "Total Capacity(MW)",
                            "कुल क्षमता (मेगावाट)",
                          )}
                        </th>
                        <th>{getText("Status", "स्थिति")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {completedPlants.length > 0 && (
                        <>
                          <tr>
                            <td colSpan="6">
                              <strong>
                                {getText(
                                  "GAS BASED POWER STATION",
                                  "गैस आधारित विद्युत स्टेशन",
                                )}
                              </strong>
                            </td>
                          </tr>
                          {completedPlants.map((item, index) => (
                            <tr key={index}>
                              <td data-key={getText("S.No.", "क्र. सं.")}>
                                {index + 1}
                              </td>
                              <td
                                data-key={getText(
                                  "Project Name",
                                  "परियोजना का नाम",
                                )}
                              >
                                <a
                                  href="#"
                                  type="button"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    navigate(
                                      `/power-generation/${item.field_category_page_assets
                                        .split(" ")
                                        .join("-")
                                        .toLowerCase()}/${item.nid}`,
                                    );
                                  }}
                                >
                                  {item.title}
                                </a>
                              </td>
                              <td
                                data-key={getText(
                                  "Location / State",
                                  "स्थान / राज्य",
                                )}
                              >
                                {item.field_location_assets}
                              </td>
                              <td
                                data-key={getText(
                                  "Installed Capacity(MW)",
                                  "स्थापित क्षमता (मेगावाट)",
                                )}
                              >
                                {item.field_installed_capacity_mw}
                              </td>
                              <td
                                data-key={getText(
                                  "Total Capacity(MW)",
                                  "कुल क्षमता (मेगावाट)",
                                )}
                              >
                                {item.field_plant_size}
                              </td>
                              <td data-key={getText("Status", "स्थिति")}>
                                {getText("Completed", "समाप्त")}
                              </td>
                            </tr>
                          ))}
                          <tr>
                            <td colSpan="4" style={{ textAlign: "right" }}>
                              <strong>
                                {getText("SUB TOTAL:", "उप-योग:")}
                              </strong>
                            </td>
                            <td colSpan="3">
                              <strong>{completedSubtotal}</strong>
                            </td>
                          </tr>
                        </>
                      )}

                      {surveyPlants.length > 0 && (
                        <>
                          <tr>
                            <td colSpan="6">
                              THERMAL POWER PROJECTS UNDER S &amp; I
                            </td>
                          </tr>
                          {surveyPlants.map((item, index) => (
                            <tr key={index}>
                              <td data-key={getText("S.No.", "क्र. सं.")}>
                                {index + 1}
                              </td>
                              <td
                                data-key={getText(
                                  "Project Name",
                                  "परियोजना का नाम",
                                )}
                              >
                                <a
                                  href="#"
                                  type="button"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    navigate(`/power-generation/${item.nid}`);
                                  }}
                                >
                                  {item.title}
                                </a>
                              </td>
                              <td
                                data-key={getText(
                                  "Location / State",
                                  "स्थान / राज्य",
                                )}
                              >
                                {item.field_location_assets}
                              </td>
                              <td
                                data-key={getText(
                                  "Installed Capacity(MW)",
                                  "स्थापित क्षमता (मेगावाट)",
                                )}
                              >
                                {item.field_installed_capacity_mw}
                              </td>
                              <td
                                data-key={getText(
                                  "Total Capacity(MW)",
                                  "कुल क्षमता (मेगावाट)",
                                )}
                              >
                                {item.field_plant_size}
                              </td>
                              <td data-key={getText("Status", "स्थिति")}>
                                Survey and Investigation (S&amp;I)
                              </td>
                            </tr>
                          ))}
                          <tr>
                            <td colSpan="4" style={{ textAlign: "right" }}>
                              <strong>SUB TOTAL: </strong>
                            </td>
                            <td colSpan="3">{surveySubtotal}</td>
                          </tr>
                        </>
                      )}

                      {underConstructionPlants.length > 0 && (
                        <>
                          <tr>
                            <td colSpan="6">
                              THERMAL POWER PROJECTS UNDER CONSTRUCTION
                            </td>
                          </tr>
                          {underConstructionPlants.map((item, index) => (
                            <tr key={index}>
                              <td data-key={getText("S.No.", "क्र. सं.")}>
                                {index + 1}
                              </td>
                              <td
                                data-key={getText(
                                  "Project Name",
                                  "परियोजना का नाम",
                                )}
                              >
                                <a
                                  href="#"
                                  type="button"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    navigate(`/power-generation/${item.nid}`);
                                  }}
                                >
                                  {item.title}
                                </a>
                              </td>
                              <td
                                data-key={getText(
                                  "Location / State",
                                  "स्थान / राज्य",
                                )}
                              >
                                {item.field_location_assets}
                              </td>
                              <td
                                data-key={getText(
                                  "Installed Capacity(MW)",
                                  "स्थापित क्षमता (मेगावाट)",
                                )}
                              >
                                {item.field_installed_capacity_mw}
                              </td>
                              <td
                                data-key={getText(
                                  "Total Capacity(MW)",
                                  "कुल क्षमता (मेगावाट)",
                                )}
                              >
                                {item.field_plant_size}
                              </td>
                              <td data-key={getText("Status", "स्थिति")}>
                                Under Construction
                              </td>
                            </tr>
                          ))}
                          <tr>
                            <td colSpan="4" style={{ textAlign: "right" }}>
                              <strong>SUB TOTAL: </strong>
                            </td>
                            <td colSpan="3">{underConstructionSubtotal}</td>
                          </tr>
                        </>
                      )}
                      <tr>
                        <td colSpan="4" style={{ textAlign: "right" }}>
                          <strong>{getText("GRAND TOTAL:", "कुल योग:")}</strong>
                        </td>
                        <td colSpan="3">
                          <strong> {grandTotal}</strong>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            <PageLastUpdated lastUpdatedOn={lastUpdatedOn} />
          </div>
        </div>
      </div>
    </>
  );
};

export default ThermalPowerPage;
